"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.process = exports.limit = exports.count = exports.distinct = exports.orderBy = void 0;
var tslib_1 = require("tslib");
var utils_1 = require("./utils");
var sort_array_operator_1 = require("./sorting/sort-array.operator");
var group_operators_1 = require("./grouping/group.operators");
var filter_operators_1 = require("./filtering/filter.operators");
var filter_expression_factory_1 = require("./filtering/filter-expression.factory");
var transducers_1 = require("./transducers");
var accessor_1 = require("./accessor");
var funcs_1 = require("./funcs");
/**
 * Orders the specified array according to the provided sort descriptors.
 *
 * @param {T[]} data - The data to be sorted.
 * @param {SortDescriptor[]} descriptors - The descriptors by which the data will be sorted.
 * @returns {T[]} - The sorted data.
 *
 * @example
 * ```ts
 * import { orderBy } from '@progress/kendo-data-query';
 *
 * const data = [
 *     { name: "Pork", category: "Food", subcategory: "Meat" },
 *     { name: "Pepper", category: "Food", subcategory: "Vegetables" },
 *     { name: "Beef", category: "Food", subcategory: "Meat" }
 * ];
 *
 * const sortDesc: SortDescriptor[] = [{ field: "name", dir: "asc" }];
 *
 * const result = orderBy(data, sortDesc);
 * ```
 */
var orderBy = function (data, descriptors) {
    if (descriptors.some(function (x) { return (0, utils_1.isPresent)(x.dir) || (0, utils_1.isPresent)(x.compare); })) {
        data = data.slice(0);
        var comparer = (0, sort_array_operator_1.composeSortDescriptors)(descriptors);
        data.sort(comparer);
    }
    return data;
};
exports.orderBy = orderBy;
var defaultComparer = function (a, b) { return a === b; };
var normalizeComparer = function (comparer) {
    if ((0, utils_1.isString)(comparer)) {
        var accessor_2 = (0, accessor_1.getter)(comparer);
        comparer = function (a, b) { return accessor_2(a) === accessor_2(b); };
    }
    return comparer;
};
var _distinct = function (data, comparer) {
    return data.filter(function (x, idx, xs) { return xs.findIndex(comparer.bind(null, x)) === idx; });
};
/**
 * Reduces the provided array so it contains only unique values.
 *
 * @param {T[]} data - The array that will be reduced.
 * @param {(Comparer | string)} comparer - An optional custom comparer function or the field name that will be used for comparison.
 * @returns {T[]} - The reduced data.
 *
 * @example
 * ```ts
 * import { distinct } from '@progress/kendo-data-query';
 *
 * const data = [
 *     { name: "Pork", category: "Food", subcategory: "Meat" },
 *     { name: "Pepper", category: "Food", subcategory: "Vegetables" },
 *     { name: "Beef", category: "Food", subcategory: "Meat" }
 * ];
 *
 * const result = distinct(data, "subcategory");
 *
 * // output:
 * // result => [
 * //     { name: "Pork", category: "Food", subcategory: "Meat" },
 * //     { name: "Pepper", category: "Food", subcategory: "Vegetables" }
 * // ];
 * ```
 */
var distinct = function (data, comparer) {
    if (comparer === void 0) { comparer = defaultComparer; }
    return _distinct(data, normalizeComparer(comparer));
};
exports.distinct = distinct;
/**
 * @hidden
 */
var count = function (data, predicate) {
    var counter = 0;
    for (var idx = 0, length_1 = data.length; idx < length_1; idx++) {
        if (predicate(data[idx])) {
            counter++;
        }
    }
    return counter;
};
exports.count = count;
/**
 * @hidden
 */
var limit = function (data, predicate) {
    if (predicate) {
        return data.filter(predicate);
    }
    return data;
};
exports.limit = limit;
/**
 * Applies the specified operation descriptors to the data.
 *
 * @param {T[]} data - The data to be processed.
 * @param {State} state - The operation descriptors that will be applied to the data.
 * @returns {DataResult} - The processed data.
 *
 * @example
 * ```ts
 *
 * const result = process(data, {
 *     skip: 10,
 *     take: 20,
 *     group: [{
 *       field: 'category.categoryName',
 *             aggregates: [
 *                   { aggregate: "sum", field: "unitPrice" },
 *                   { aggregate: "sum", field: "unitsInStock" }
 *             ]
 *       }],
 *     sort: [{ field: 'productName', dir: 'desc' }],
 *     filter: {
 *         logic: "or",
 *         filters: [
 *           { field: "discontinued", operator: "eq", value: true },
 *           { field: "unitPrice", operator: "lt", value: 22 }
 *         ]
 *     }
 * });
 *
 * ```
 */
var process = function (data, state) {
    var skipCount = state.skip, takeCount = state.take, filterDescriptor = state.filter, sort = state.sort, group = state.group;
    var sortDescriptors = tslib_1.__spreadArray(tslib_1.__spreadArray([], (0, group_operators_1.normalizeGroups)(group || []), true), sort || [], true);
    if (sortDescriptors.length) {
        data = (0, exports.orderBy)(data, sortDescriptors);
    }
    var hasFilters = (0, utils_1.isPresent)(filterDescriptor) && transducers_1.filter.length;
    var hasGroups = (0, utils_1.isPresent)(group) && group.length;
    if (!hasFilters && !hasGroups) {
        return {
            data: takeCount ? data.slice(skipCount, skipCount + takeCount) : data,
            total: data.length
        };
    }
    var total;
    var transformers = [];
    var predicate;
    if (hasFilters) {
        predicate = (0, filter_expression_factory_1.compileFilter)((0, filter_operators_1.normalizeFilters)(filterDescriptor));
        total = (0, exports.count)(data, predicate);
        transformers.push((0, transducers_1.filter)(predicate));
    }
    else {
        total = data.length;
    }
    if ((0, utils_1.isPresent)(skipCount) && (0, utils_1.isPresent)(takeCount)) {
        transformers.push((0, transducers_1.skip)(skipCount));
        transformers.push((0, transducers_1.take)(takeCount));
    }
    if (transformers.length) {
        var transform = funcs_1.compose.apply(void 0, transformers);
        var result = hasGroups ?
            (0, group_operators_1.groupBy)(data, group, transform, (0, exports.limit)(data, predicate)) :
            (0, transducers_1.exec)(transform(transducers_1.concat), [], data);
        return { data: result, total: total };
    }
    return {
        data: hasGroups ? (0, group_operators_1.groupBy)(data, group) : data,
        total: total
    };
};
exports.process = process;



//////////////////
// WEBPACK FOOTER
// /Users/larsmarin/Documents/Workspaces/AAG/aag-amc-projects/~/.pnpm/@progress+kendo-data-query@1.7.1/~/@progress/kendo-data-query/dist/npm/array.operators.js
// module id = 6411
// module chunks = 0